import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";
import { EmptyState } from "../components/EmptyState";
import { isAbortError } from "../lib/asyncState";

interface SavedBrandRow {
  id: string;
  slug: string;
  name: string;
  short_summary: string | null;
  country: string | null;
  saved_at: string;
  note: string | null;
}

export function SavedBrandsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<SavedBrandRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api.me.savedBrands({ signal: controller.signal })
      .then((r) => setItems(r.items as unknown as SavedBrandRow[]))
      .catch((err) => {
        if (isAbortError(err)) return;
        setItems([]);
        setError("Could not load your saved brands right now.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [user, reloadKey]);

  if (!user) {
    return (
      <div className="container empty-state">
        <p>Sign in to save brands.</p>
        <Link to="/login"><button className="primary">Sign in</button></Link>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBlock: "2rem" }}>
      <h2>Saved brands</h2>
      {loading ? <div className="empty-state">Loading…</div> :
        error ? (
          <EmptyState title="Saved brands could not load" actions={<button className="primary" onClick={() => setReloadKey((key) => key + 1)}>Try again</button>}>
            {error}
          </EmptyState>
        ) : items.length === 0 ? <div className="empty-state">No saved brands yet.</div> : (
          <div className="brand-grid" style={{ marginTop: "1.5rem" }}>
            {items.map((b) => (
              <Link key={b.id} to={`/brands/${b.slug}`} className="brand-card">
                <div className="brand-card__name">{b.name}</div>
                <div className="brand-card__meta">{b.country ?? "—"}</div>
                {b.short_summary && <p className="brand-card__summary">{b.short_summary}</p>}
                {b.note && <p className="brand-card__summary"><em>Note: {b.note}</em></p>}
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}
